/* LuxeLane v2 skin quiz (sections/lx-quiz.liquid).
 * Screens: intro -> questions -> matching -> results. Each Answer has a
 * key; each chosen key collects the question's importance as points.
 * Match blocks carry "key:weight" pairs; a product's score is the sum of
 * points x weight. The top N are shown ticked; ticked products (with the
 * chosen option) go to the bag in one /cart/add.js call.
 */
(() => {
  const ADVANCE_MS = 380;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function money(cents) {
    const currency = window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency }).format(cents / 100);
    } catch (_) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  function parseKeys(text) {
    return (text || '')
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => {
        const [key, weight] = part.split(':').map((s) => s.trim());
        return [key, Number(weight) || 1];
      });
  }

  function initQuiz(root) {
    if (!root || root.dataset.lxReady) return;
    root.dataset.lxReady = '1';

    const screens = Object.fromEntries([...root.querySelectorAll('[data-lx-screen]')].map((el) => [el.dataset.lxScreen, el]));
    const steps = [...root.querySelectorAll('[data-lx-step]')];
    const matches = [...root.querySelectorAll('template[data-lx-match]')];
    const els = {
      progress: root.querySelector('[data-lx-progress]'),
      count: root.querySelector('[data-lx-count]'),
      back: root.querySelector('[data-lx-back]'),
      next: root.querySelector('[data-lx-next]'),
      skip: root.querySelector('[data-lx-skip]'),
      meta: root.querySelector('[data-lx-meta]'),
      grid: root.querySelector('[data-lx-grid]'),
      title: root.querySelector('[data-lx-results-title]'),
      picked: root.querySelector('[data-lx-picked]'),
      total: root.querySelector('[data-lx-total]'),
      addAll: root.querySelector('[data-lx-add-all]'),
      addLabel: root.querySelector('[data-lx-add-label]'),
      error: root.querySelector('[data-lx-quiz-error]'),
    };
    let current = 0;

    if (els.meta && steps.length) {
      els.meta.textContent = `${steps.length} quick questions · about a minute`;
    }

    /* ---------- screens ---------- */
    function show(name) {
      Object.entries(screens).forEach(([key, el]) => {
        const on = key === name;
        el.hidden = !on;
        el.classList.toggle('is-active', on);
      });
      const top = root.getBoundingClientRect().top + window.scrollY - 100;
      if (window.scrollY > top) {
        if (window.LxLenis) window.LxLenis.scrollTo(top);
        else window.scrollTo({ top, behavior: reduceMotion ? 'auto' : 'smooth' });
      }
    }

    /* ---------- questions ---------- */
    const picked = (step) => [...step.querySelectorAll('[data-lx-answer][aria-pressed="true"]')];

    function goTo(index, back = false) {
      current = Math.max(0, Math.min(index, steps.length - 1));
      steps.forEach((step, i) => {
        step.classList.toggle('is-current', i === current);
        step.classList.toggle('is-back', i === current && back);
      });
      els.progress.style.transform = `scaleX(${(current + 1) / steps.length})`;
      els.count.textContent = `${current + 1} / ${steps.length}`;
      els.back.disabled = current === 0;
      updateNav();
      steps[current].querySelector('[data-lx-answer]')?.focus({ preventScroll: true });
    }

    function updateNav() {
      const step = steps[current];
      const answered = picked(step).length > 0;
      els.next.disabled = !answered;
      els.next.querySelector('span').textContent = current === steps.length - 1 ? 'See my matches' : 'Next';
      els.skip.hidden = !step.hasAttribute('data-optional') || answered;
    }

    function next() {
      if (current < steps.length - 1) goTo(current + 1);
      else finish();
    }

    root.addEventListener('click', (event) => {
      const answer = event.target.closest('[data-lx-answer]');
      if (!answer) return;
      const step = answer.closest('[data-lx-step]');
      if (step.hasAttribute('data-multiple')) {
        answer.setAttribute('aria-pressed', String(answer.getAttribute('aria-pressed') !== 'true'));
        updateNav();
        return;
      }
      step.querySelectorAll('[data-lx-answer]').forEach((a) => a.setAttribute('aria-pressed', String(a === answer)));
      updateNav();
      const at = current;
      setTimeout(() => current === at && next(), reduceMotion ? 0 : ADVANCE_MS);
    });

    root.querySelector('[data-lx-start]')?.addEventListener('click', () => {
      if (!steps.length) return;
      show('flow');
      goTo(0);
    });
    els.next.addEventListener('click', next);
    els.skip.addEventListener('click', next);
    els.back.addEventListener('click', () => goTo(current - 1, true));

    /* ---------- scoring ---------- */
    function score() {
      const points = {};
      let title = '';
      steps.forEach((step) => {
        const weight = Number(step.dataset.weight) || 1;
        picked(step).forEach((answer) => {
          const key = answer.dataset.key;
          if (key) points[key] = (points[key] || 0) + weight;
          if (!title && answer.dataset.resultTitle) title = answer.dataset.resultTitle;
        });
      });
      const ranked = matches
        .map((tpl, order) => ({
          tpl,
          order,
          score: parseKeys(tpl.dataset.keys).reduce((sum, [key, w]) => sum + (points[key] || 0) * w, 0),
        }))
        .sort((a, b) => b.score - a.score || a.order - b.order);
      const limit = Number(root.dataset.results) || 3;
      let top = ranked.filter((m) => m.score > 0).slice(0, limit);
      if (!top.length) top = ranked.slice(0, limit);
      return { top, title };
    }

    /* ---------- results ---------- */
    function cardPrice(card) {
      const field = card.querySelector('[data-lx-variant]');
      const option = field?.tagName === 'SELECT' ? field.selectedOptions[0] : field;
      return Number(option?.dataset.price) || 0;
    }

    function refreshTotals() {
      const cards = [...els.grid.querySelectorAll('.lx-quiz-card')];
      const chosen = cards.filter((card) => card.querySelector('[data-lx-pick]').checked);
      cards.forEach((card) => {
        card.classList.toggle('is-off', !card.querySelector('[data-lx-pick]').checked);
        card.querySelector('[data-lx-card-price]').textContent = money(cardPrice(card));
      });
      const total = chosen.reduce((sum, card) => sum + cardPrice(card), 0);
      els.picked.textContent = `${chosen.length} of ${cards.length} selected`;
      els.total.textContent = money(total);
      els.addAll.disabled = chosen.length === 0;
      els.addLabel.textContent = chosen.length === cards.length && cards.length > 1 ? `Add all ${chosen.length} to bag` : `Add ${chosen.length} to bag`;
    }

    function finish() {
      show('loading');
      const wait = reduceMotion ? 200 : Number(root.dataset.loadingMs) || 1600;
      setTimeout(() => {
        const { top, title } = score();
        els.grid.replaceChildren();
        top.forEach((match, i) => {
          const card = match.tpl.content.firstElementChild.cloneNode(true);
          card.style.setProperty('--i', i);
          if (i === 0 && match.score > 0) card.querySelector('[data-lx-rank]').hidden = false;
          els.grid.append(card);
        });
        els.title.textContent = title || root.dataset.resultsTitle;
        els.error.hidden = true;
        els.addAll.querySelector('[data-lx-add-label]').textContent = '';
        refreshTotals();
        show('results');
        els.title.focus({ preventScroll: true });
      }, wait);
    }

    els.grid.addEventListener('change', refreshTotals);

    els.addAll.addEventListener('click', async () => {
      const items = [...els.grid.querySelectorAll('.lx-quiz-card')]
        .filter((card) => card.querySelector('[data-lx-pick]').checked)
        .map((card) => ({ id: Number(card.querySelector('[data-lx-variant]').value), quantity: 1 }));
      if (!items.length) return;
      els.addAll.disabled = true;
      els.error.hidden = true;
      try {
        const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.description || data.message || 'Could not add to your bag.');
        els.addLabel.textContent = 'Added ✓';
        await window.LxCart?.refreshCartCount();
        document.dispatchEvent(new CustomEvent('lx:cart-updated')); // refreshes + opens the drawer
      } catch (error) {
        els.error.textContent = error.message;
        els.error.hidden = false;
      } finally {
        setTimeout(() => {
          els.addAll.disabled = false;
          refreshTotals();
        }, 1600);
      }
    });

    root.querySelector('[data-lx-retake]')?.addEventListener('click', () => {
      root.querySelectorAll('[data-lx-answer]').forEach((a) => a.setAttribute('aria-pressed', 'false'));
      show('flow');
      goTo(0);
    });

    // Theme editor: jump to the question being edited.
    root.addEventListener('shopify:block:select', (event) => {
      const step = event.target.closest('[data-lx-step]');
      if (!step) return;
      show('flow');
      goTo(steps.indexOf(step));
    });
  }

  const boot = () => document.querySelectorAll('[data-lx-quiz]').forEach(initQuiz);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', (e) => e.target.querySelectorAll('[data-lx-quiz]').forEach(initQuiz));
})();
